import {AbstractControl} from '@angular/forms';
import {
  FieldBlock, FormBlock, FormDefinition, GroupBlock, ListBlock, StaticTextBlock, supportsCondition
} from '../models/form-schema.model';
import {RulesEngineService} from './rules-engine.service';

/**
 * Whole-definition conditional-logic evaluation (#470).
 *
 * **Why this exists.** Visibility used to be decided per *rendered* block, inside `BlockRendererComponent`. A
 * multi-page wizard only keeps the current page in the DOM, so every renderer on the other pages is destroyed —
 * and with it the only thing that ever disabled a hidden, required control. Change an upstream answer after
 * leaving a page and its now-hidden required fields stay **enabled and empty**: `form.valid` is false forever,
 * nothing on screen says why, and the employee cannot submit. Evaluating the *definition* instead of the DOM is
 * the fix; everything else here follows from that.
 *
 * Two facts are deliberately kept apart, because they mean different things downstream:
 * - **`visibleWhen` false ⇒ the field does not apply.** It is neutralised (disabled so it cannot block the form)
 *   and it is omitted from the submission entirely (see `buildSubmitRequest`).
 * - **`enabledWhen` false ⇒ the field applies but is currently read-only.** It keeps its value and it is still
 *   submitted.
 *
 * The evaluation is a single pass over the current raw value, exactly like the per-block evaluation it replaces:
 * a rule reads whatever the form holds *now*, and there is no fixed-point iteration over chained rules. Ordering
 * therefore does not matter, and a change that flips one rule re-runs the whole pass anyway.
 */

/** Rule evaluation is stateless, so one shared instance serves the plain-function API (no DI needed). */
const DEFAULT_RULES = new RulesEngineService();

/** A leaf (value-bearing, non-container) block evaluated in one concrete scope — one list row is one scope. */
export interface FieldVisibility {
  /** The block's own, globally unique field key. */
  key: string;
  /**
   * Path from the root form to this field's control, **as segments**. Always used with the array overload of
   * `AbstractControl.get`, which does not split on `.` — see {@link controlAtPath}.
   */
  path: string[];
  /** False when the field does not apply: its own `visibleWhen` is false, or an enclosing container is hidden. */
  visible: boolean;
  /**
   * False when the field applies but is currently read-only — either its own `enabledWhen` says so, or an
   * **enclosing container's** does (#492). Independent of {@link visible}: a read-only field still applies, so
   * it is still rendered and still submitted; only `visibleWhen` means "does not apply".
   */
  enabled: boolean;
  /** Whether the field is currently required (static `required`, or `requiredWhen` evaluating true). */
  required: boolean;
  /** The block itself, so consumers do not have to walk the definition again. */
  block: FieldBlock;
}

/**
 * The path segment a block is addressed by — its `key`, falling back to its `id`.
 *
 * **Both** the evaluator (which writes verdict entries) and `BlockRendererComponent.blockPath()` (which looks
 * them up) must derive the segment the same way. #490 was not caused by disagreement here, but by the evaluator
 * skipping a block type entirely; the shared helper exists so the *next* change cannot introduce a mismatch,
 * which would fail exactly as silently — an entry written under one path and read under another.
 *
 * The `id` fallback covers a stored definition whose block carries no key. `StaticTextBlock.key` is required by
 * the model, but a hand-edited or imported document is not bound by the model.
 */
export function blockPathSegment(block: FormBlock): string {
  const key = (block as { key?: string }).key;
  return key ? key : block.id;
}

/** A `static-text` block evaluated in one concrete scope. It carries no value, but it does carry rules (#490). */
export interface StaticTextVisibility {
  key: string;
  path: string[];
  visible: boolean;
  block: StaticTextBlock;
}

/** A `group` / `list` container block evaluated in one concrete scope. */
export interface ContainerVisibility {
  key: string;
  path: string[];
  visible: boolean;
  /**
   * False when the container applies but its whole subtree is currently read-only (`enabledWhen`), or when an
   * enclosing container is itself disabled. Independent of {@link visible} — see {@link FieldVisibility.enabled}.
   */
  enabled: boolean;
  /**
   * Whether the container's own `requiredWhen` currently holds (#493).
   *
   * Only meaningful for a `list`: `Validators.required` on a `FormArray` rejects an **empty** array, because
   * Angular's `isEmptyInputValue` runs `lengthOrSize`, which returns `value.length` for an array. That is the
   * only thing in the codebase that makes a repeating list "at least one entry" — `minItems` seeds rows and
   * gates the remove button, it never validates. On a `FormGroup` the same validator is a permanent no-op
   * (`lengthOrSize` returns `null` for a plain object, so `isEmptyInputValue` is always false), which is why
   * `group` does not offer `requiredWhen` at all.
   */
  required: boolean;
  block: GroupBlock | ListBlock;
}

/** The verdict for a whole definition at one point in time. */
export interface VisibilityVerdict {
  /** Every leaf field, in definition order, one entry per concrete list row. */
  fields: FieldVisibility[];
  /** Every `group` / `list` container, in definition order. */
  containers: ContainerVisibility[];
  /** Every `static-text` block, in definition order (#490). */
  staticTexts: StaticTextVisibility[];
  /** Lookup by {@link pathId}. */
  byPath: Map<string, FieldVisibility | ContainerVisibility | StaticTextVisibility>;
}

/**
 * A collision-free string id for a control path.
 *
 * A plain `join('.')` would be ambiguous the moment a key legitimately contains a dot — which is exactly the
 * class of bug this change exists to fix — so the separator is a character that cannot occur in a key.
 */
export function pathId(path: readonly string[]): string {
  return path.join('\u0000');
}

/**
 * Resolves a control from a path produced by {@link evaluateFormVisibility}.
 *
 * **Always the array overload.** `AbstractControl.get('a.b')` splits its argument on `.` (Angular's
 * `AbstractControl._find`), so a field key containing a dot — say `Verzicht zus. Rentenversicherung` — resolves
 * to `null` even though the control exists under exactly that literal name. The array form treats each segment
 * verbatim, which is the only lookup that is correct for author-supplied keys.
 */
export function controlAtPath(root: AbstractControl, path: readonly string[]): AbstractControl | null {
  return root.get([...path]);
}

/** The empty verdict, for callers with no definition yet. */
export function emptyVerdict(): VisibilityVerdict {
  return {fields: [], containers: [], staticTexts: [], byPath: new Map()};
}

/**
 * Evaluates every block of `definition` against `rawValue` (the form's `getRawValue()`, i.e. **including**
 * disabled controls — a hidden field's stored value must still be readable by the rules that hide it).
 *
 * List rows reproduce {@link BlockRendererComponent}'s scope-overlay semantics exactly: the root value is the
 * base, and each enclosing `group` / list-row scope overlays its own **leaf** fields by bare key, innermost
 * last. So a rule inside a repeating row that references a sibling key resolves to *that row's* value, not to a
 * top-level field that happens to share the key.
 *
 * @param definition the form definition to evaluate
 * @param rawValue the form's raw value (nested by group key, arrays for list blocks)
 * @param rules the rules engine to evaluate with; defaults to a shared stateless instance
 */
export function evaluateFormVisibility(
  definition: FormDefinition | null | undefined,
  rawValue: any,
  rules: RulesEngineService = DEFAULT_RULES): VisibilityVerdict {

  const verdict = emptyVerdict();
  if (!definition) return verdict;

  const root = rawValue ?? {};

  /**
   * Builds the value context for one scope: the root value, with every enclosing scope's leaf fields overlaid
   * by bare key. The root scope itself needs no overlay — its leaf keys already sit at the top level.
   */
  const contextFor = (scopes: { blocks: FormBlock[]; value: any }[]): any => {
    if (scopes.length === 0) return root;
    const context = {...root};
    for (const scope of scopes) {
      for (const block of scope.blocks) {
        if (block.type === 'static-text' || block.type === 'group' || block.type === 'list') continue;
        const key = (block as FieldBlock).key;
        context[key] = scope.value?.[key];
      }
    }
    return context;
  };

  const walk = (
    blocks: FormBlock[],
    scopeValue: any,
    parentPath: string[],
    ancestorVisible: boolean,
    ancestorEnabled: boolean,
    scopes: { blocks: FormBlock[]; value: any }[]) => {

    const context = contextFor(scopes);

    for (const block of blocks) {
      // NOTE: static-text is NOT skipped here (#490). It carries no value, but it does carry `visibleWhen`, and
      // skipping it left every conditional text block permanently on screen. The only correct skip is the one in
      // `contextFor` above, which excludes it from the *value* overlay.
      const key = blockPathSegment(block);
      const path = [...parentPath, key];
      // An ancestor container that does not apply takes its whole subtree with it — a field inside a hidden
      // group is hidden even though its own visibleWhen says nothing.
      const visible = ancestorVisible
        && (!block.visibleWhen || rules.evaluateRuleGroup(block.visibleWhen, context));
      // The same containment on the *enabled* axis (#492). Threading it here rather than relying on the order
      // the consequences are applied is what makes "an ancestor that is disabled beats a child that says
      // enabled" a property of the verdict itself: a child inside a disabled container simply never reports
      // `enabled: true`, so nothing downstream can re-enable it — which in Angular would also silently flip the
      // parent FormGroup back to enabled, since a group is only DISABLED while *all* its children are.
      const enabled = ancestorEnabled
        && (!block.enabledWhen || rules.evaluateRuleGroup(block.enabledWhen, context));
      // Containers carry no static `required` flag of their own, so this is purely the rule (#493) — and only
      // where the block type can honour it at all. The matrix is the single source of truth for that: a stored
      // `requiredWhen` on a `group` is inert (the validator cannot fail on a FormGroup), and the verdict says so
      // rather than claiming a requirement nothing will enforce.
      const containerRequired = supportsCondition(block.type, 'requiredWhen')
        && !!block.requiredWhen && rules.evaluateRuleGroup(block.requiredWhen, context);

      if (block.type === 'group') {
        const groupBlock = block as GroupBlock;
        const value = scopeValue?.[key] ?? {};
        const entry: ContainerVisibility =
          {key, path, visible, enabled, required: containerRequired, block: groupBlock};
        verdict.containers.push(entry);
        verdict.byPath.set(pathId(path), entry);
        walk(groupBlock.blocks, value, path, visible, enabled, [...scopes, {blocks: groupBlock.blocks, value}]);
      } else if (block.type === 'list') {
        const listBlock = block as ListBlock;
        const entry: ContainerVisibility =
          {key, path, visible, enabled, required: containerRequired, block: listBlock};
        verdict.containers.push(entry);
        verdict.byPath.set(pathId(path), entry);

        const rows: unknown[] = Array.isArray(scopeValue?.[key]) ? scopeValue[key] : [];
        rows.forEach((row, index) => {
          const value = (row ?? {}) as any;
          walk(listBlock.blocks, value, [...path, String(index)], visible, enabled,
            [...scopes, {blocks: listBlock.blocks, value}]);
        });
      } else if (block.type === 'static-text') {
        const entry: StaticTextVisibility = {key, path, visible, block: block as StaticTextBlock};
        verdict.staticTexts.push(entry);
        verdict.byPath.set(pathId(path), entry);
      } else {
        const field = block as FieldBlock;
        const entry: FieldVisibility = {
          key,
          path,
          visible,
          enabled,
          // A statically-required field is never un-required by a requiredWhen that happens to be false.
          required: field.required === true
            || (!!field.requiredWhen && rules.evaluateRuleGroup(field.requiredWhen, context)),
          block: field
        };
        verdict.fields.push(entry);
        verdict.byPath.set(pathId(path), entry);
      }
    }
  };

  definition.pages.forEach(page => walk(page.blocks, root, [], true, true, []));
  return verdict;
}
